//* Vue
import type { SetupContext } from "vue";
//* 组件属性
import type { UiSelectProps, UiSelectEmits } from "../index";
//* 公共函数
import { node, utility, dispose, animations } from "various-ui";

export const useComposable = (define: UiSelectProps, emits: SetupContext<typeof UiSelectEmits>["emit"]) => {
    //* 静态变量
    const vars: { observer?: ResizeObserver | undefined } = {};
    //* 响应式变量
    const refs = {
        visible: ref<boolean>(false),
    };

    const nodes = {
        body: ref<HTMLElement>(),
        container: ref<HTMLElement>(),
    };

    //* 函数列表
    const methods = {
        //* 候选框隐藏事件
        hidden: (ev?: Event) => {
            if (!nodes.container.value) return;
            if (ev?.target && node.includes(ev.target as HTMLElement, nodes.container.value)) return;
            else {
                //* 状态更新
                refs.visible.value = false;
                //* 注销观察者
                vars.observer?.disconnect();
            }
        },

        //* 候选框显示事件
        show: () => {
            //* 判断选择框是否处于异常状态
            if (define.readonly || define.disabled) return;
            //* 判断候选项是否已被激活
            if (refs.visible.value) methods.hidden();
            else {
                refs.visible.value = true;
                nextTick(() => {
                    if (!nodes.container.value || !nodes.body.value) return;
                    else {
                        //* 隐藏事件
                        addEventListener("click", methods.hidden, { capture: true, once: true });
                        //* 将内容添加到视图容器中
                        node.append(document.body, nodes.body.value);
                        //* 注册观察者
                        vars.observer?.disconnect();
                        vars.observer = new ResizeObserver(() => {
                            if (!nodes.container.value || !nodes.body.value) return;
                            else {
                                //* 数据初始化
                                const data = { container: nodes.container.value, view: nodes.body.value };
                                const width = nodes.container.value?.offsetWidth || 0;
                                dispose.boundary.relativeContainerBody(data, {
                                    direction: "bottom",
                                    offset: 8,
                                    width: { min: width },
                                    align: "start",
                                });
                            }
                        });

                        vars.observer.observe(document.documentElement);
                    }
                });
            }
        },
    };

    //* 计算属性
    const computeds = {
        //* 组件值
        value: computed(() => {
            for (const i in Contrast.Protein) {
                if (Contrast.Protein[i]?.codons.includes(define.modelValue)) {
                    return `${i}(${Contrast.Protein[i]?.shortnanme}): ` + define.modelValue;
                }
            }
            return "";
        }),

        //* 组件类
        className: computed(() => {
            //* 初始化数据
            const className: string[] = [];
            //* 判断候选项是否处于展示状态
            if (refs.visible.value) className.push("ui-form-selector");
            //* 判断是否需要添加size类名
            if (define.size != "default") className.push(`ui-${define.size}`);
            //* 判断是否是禁用或只读状态
            if (define.disabled) className.push("ui-disabled-status");
            else if (define.readonly) {
                className.push("ui-readonly-status");
            }

            return className.join(" ");
        }),

        //* 匹配对照表数据
        candidates: computed(() => {
            if (!define.matchs.length) return Contrast.Protein;
            else {
                const result: { [name: string]: { name: string; codons: string[]; fullname: string; shortnanme: string } } = {};
                for (const i in Contrast.Protein) {
                    if (Contrast.Protein[i]?.codons.find((codon) => define.matchs.includes(codon))) {
                        result[i] = Contrast.Protein[i];
                    }
                }

                return result;
            }
        }),
    };

    //* 属性
    const binds = reactive({
        //* 主体
        main: computed(() => {
            return {
                value: computeds.value.value,
                disabled: define.disabled,
                placeholder: define.placeholder,
                autocomplete: "off",
            };
        }),

        //* 容器
        container: computed(() => {
            return {
                class: computeds.className.value,
                style: {
                    width: utility.isNumber(define.width) ? define.width + "px" : define.width,
                },
            };
        }),
    });

    //* 响应事件
    const ons = {
        //* 候选项容器事件
        animation: animations.selector(true),
        //* 候选项事件
        candidate: (codon: string) => {
            return {
                //* 候选项选择事件
                mousedown: (ev: Event) => {
                    emits("update:modelValue", codon);
                    emits("change", ev);
                    if (refs.visible.value) {
                        methods.hidden();
                    }
                },
            };
        },
    };

    return { ons, vars, refs, nodes, binds, methods, computeds };
};
